import React, { useState, useEffect } from 'react';
import DataService from '../services/dataService';
import { Pooja, GalleryItem, SiteConfig, Banner } from '../types';
import { Trash2, Plus, Save, LogOut, Download, RefreshCw, Pencil, Image as ImageIcon, Home, Bell, AlignLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_BANNERS } from '../constants';

const Admin: React.FC = () => {

// Sync status for Admin UI (added by patch)
const [syncStatus, setSyncStatus] = useState<{state:string, message?:string}>({ state: 'idle' });

useEffect(() => {
  const onStart = (e: any) => {
    setSyncStatus({ state: "saving" });
    console.log("[Admin] save_started", e?.detail);
  };

  const onSuccess = (e: any) => {
    setSyncStatus({ state: "saved" });
    console.log("[Admin] save_success", e?.detail);
    setTimeout(() => setSyncStatus({ state: "idle" }), 1800);
  };

  const onError = (e: any) => {
    setSyncStatus({
      state: "error",
      message: e?.detail?.error || e?.detail?.status || "Unknown",
    });
    console.error("[Admin] save_error", e?.detail);
  };

  window.addEventListener("save_started", onStart as EventListener);
  window.addEventListener("save_success", onSuccess as EventListener);
  window.addEventListener("save_error", onError as EventListener);

  return () => {
    window.removeEventListener("save_started", onStart as EventListener);
    window.removeEventListener("save_success", onSuccess as EventListener);
    window.removeEventListener("save_error", onError as EventListener);
  };
}, []);


  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  
  const [activeTab, setActiveTab] = useState<'general' | 'home' | 'poojas' | 'gallery'>('general');
  const [config, setConfig] = useState<SiteConfig>(DataService.getConfig());
  const [banners, setBanners] = useState<Banner[]>([]);
  const [poojas, setPoojas] = useState<Pooja[]>([]);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);

  // Editing States
  const [editingPooja, setEditingPooja] = useState<Pooja | null>(null);

  // Simple auth check
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'admin555') {
      setIsAuthenticated(true);
      loadData();
    } else {
      alert('Invalid password ');
    }
  };

  const loadData = () => {
    setConfig(DataService.getConfig());
    setBanners(DataService.getBanners());
    setPoojas(DataService.getPoojas());
    setGallery(DataService.getGallery());
  };

  // --- Home / Banner Handlers ---
  const handleBannerChange = (field: keyof Banner, value: string) => {
    // Modify local state only
    const newBanners = [...banners];
    if ((newBanners?.length ?? 0) === 0) {
      newBanners.push({ ...DEFAULT_BANNERS[0] });
    }
    newBanners[0] = { ...newBanners[0], [field]: value };
    setBanners(newBanners);
  };

  // --- Announcement Handlers ---
  const handleAddAnnouncement = () => {
    const newConfig = { ...config };
    newConfig.announcements = [...(newConfig.announcements || []), "New Announcement"];
    newConfig.announcementsMal = [...(newConfig.announcementsMal || []), "പുതിയ അറിയിപ്പ്"];
    setConfig(newConfig);
  };

  const handleUpdateAnnouncement = (index: number, lang: 'en' | 'ml', value: string) => {
    const newConfig = { ...config };
    if (lang === 'en') {
        const arr = [...(newConfig.announcements || [])];
        arr[index] = value;
        newConfig.announcements = arr;
    } else {
        const arr = [...(newConfig.announcementsMal || [])];
        arr[index] = value;
        newConfig.announcementsMal = arr;
    }
    setConfig(newConfig);
  };

  const handleDeleteAnnouncement = (index: number) => {
    const newConfig = { ...config };
    const arrEn = [...(newConfig.announcements || [])];
    const arrMal = [...(newConfig.announcementsMal || [])];
    
    arrEn.splice(index, 1);
    arrMal.splice(index, 1);
    
    newConfig.announcements = arrEn;
    newConfig.announcementsMal = arrMal;
    setConfig(newConfig);
  };

  const handleHomeSettingsSave = () => {
    // Save Config (Announcements, About Text)
    DataService.updateConfig(config);
    // Save Banner
    if ((banners?.length ?? 0) > 0) {
        DataService.updateBanner(banners[0]);
    }
    alert('Home page settings saved successfully!');
  };

  // --- Pooja Handlers ---
  const handleAddPooja = () => {
    const newPooja: Pooja = {
      id: Date.now().toString(),
      name: 'New Pooja',
      nameMal: 'പുതിയ പൂജ',
      description: 'Description here',
      descriptionMal: 'വിവരണം ഇവിടെ',
      price: 100,
      timing: 'Morning',
      timingMal: 'രാവിലെ'
    };
    DataService.addPooja(newPooja);
    setPoojas(DataService.getPoojas());
    setEditingPooja(newPooja); // Auto open edit
  };

  const handleUpdatePooja = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPooja) {
        DataService.updatePooja(editingPooja);
        setPoojas(DataService.getPoojas());
        setEditingPooja(null);
        alert('Pooja updated successfully!');
    }
  };

  const handleDeletePooja = (id: string) => {
    if(window.confirm("Delete this pooja?")) {
      DataService.deletePooja(id);
      setPoojas(DataService.getPoojas());
    }
  };

  // --- Gallery Handlers ---
  const handleAddGallery = () => {
    const url = prompt("Enter Image URL (local path like '/images/photo.jpg' or http link):");
    if (!url) return;
    
    const newRef: GalleryItem = {
      id: Date.now().toString(),
      url: url,
      caption: 'New Image',
      captionMal: 'പുതിയ ചിത്രം'
    };
    DataService.addGalleryItem(newRef);
    setGallery(DataService.getGallery());
  };

  const handleUpdateGalleryCaption = (id: string, field: 'caption' | 'captionMal', value: string) => {
     const item = gallery.find(g => g.id === id);
     if (item) {
         const updated = { ...item, [field]: value };
         DataService.updateGalleryItem(updated);
         setGallery(DataService.getGallery());
     }
  };

  const handleDeleteGallery = (id: string) => {
    if(window.confirm("Delete this image?")) {
      DataService.deleteGalleryItem(id);
      setGallery(DataService.getGallery());
    }
  };

  // --- Config Handlers ---
  const handleConfigSave = () => {
    DataService.updateConfig(config);
    alert('General configuration saved!');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-temple-cream">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md border border-temple-orange-200">
          <h2 className="text-2xl font-bold mb-6 text-center text-temple-orange-900">Admin Login</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <input 
              type="password" 
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none"
            />
            <button type="submit" className="w-full bg-temple-orange-600 text-white py-2 rounded font-bold hover:bg-temple-orange-700">
              Login
            </button>            
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans">
      {/* Admin Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-800">Admin Dashboard</h1>
            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">v1.1</span>
        </div>
        <div className="flex gap-4">
            <button onClick={DataService.exportData} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
                <Download size={16} /> Backup
            </button>
            <button onClick={() => {DataService.resetData(); window.location.reload()}} className="flex items-center gap-2 text-sm text-red-600 hover:text-red-800 font-medium">
                <RefreshCw size={16} /> Reset
            </button>
            <button onClick={() => {setIsAuthenticated(false); navigate('/')}} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 font-medium">
                <LogOut size={16} /> Logout
            </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
            {/* Sidebar */}
            <div className="w-full md:w-64 flex-shrink-0">
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
                    <button 
                        onClick={() => setActiveTab('general')}
                        className={`w-full text-left px-6 py-4 font-medium border-b hover:bg-gray-50 transition-colors ${activeTab === 'general' ? 'bg-orange-50 text-orange-700 border-l-4 border-l-orange-500' : ''}`}
                    >
                        General Settings
                    </button>
                    <button 
                        onClick={() => setActiveTab('home')}
                        className={`w-full text-left px-6 py-4 font-medium border-b hover:bg-gray-50 transition-colors ${activeTab === 'home' ? 'bg-orange-50 text-orange-700 border-l-4 border-l-orange-500' : ''}`}
                    >
                        Home Page Settings
                    </button>
                    <button 
                        onClick={() => setActiveTab('poojas')}
                        className={`w-full text-left px-6 py-4 font-medium border-b hover:bg-gray-50 transition-colors ${activeTab === 'poojas' ? 'bg-orange-50 text-orange-700 border-l-4 border-l-orange-500' : ''}`}
                    >
                        Manage Poojas
                    </button>
                    <button 
                        onClick={() => setActiveTab('gallery')}
                        className={`w-full text-left px-6 py-4 font-medium hover:bg-gray-50 transition-colors ${activeTab === 'gallery' ? 'bg-orange-50 text-orange-700 border-l-4 border-l-orange-500' : ''}`}
                    >
                        Gallery Images
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1">
                {activeTab === 'general' && (
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">General Configuration</h2>
                        <div className="space-y-4 max-w-2xl">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Temple Name (English)</label>
                                <input 
                                    value={config.templeName} 
                                    onChange={(e) => setConfig({...config, templeName: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Temple Name (Malayalam)</label>
                                <input 
                                    value={config.templeNameMal || ''} 
                                    onChange={(e) => setConfig({...config, templeNameMal: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <input 
                                        value={config.phone} 
                                        onChange={(e) => setConfig({...config, phone: e.target.value})}
                                        className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                                    <input 
                                        value={config.whatsappNumber} 
                                        onChange={(e) => setConfig({...config, whatsappNumber: e.target.value})}
                                        className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input 
                                    value={config.email} 
                                    onChange={(e) => setConfig({...config, email: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
                                <input 
                                    value={config.upiId} 
                                    onChange={(e) => setConfig({...config, upiId: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
                                <input 
                                    value={config.facebookUrl || ''} 
                                    onChange={(e) => setConfig({...config, facebookUrl: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                    placeholder="https://facebook.com/..."
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                                <input 
                                    value={config.youtubeUrl || ''} 
                                    onChange={(e) => setConfig({...config, youtubeUrl: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                    placeholder="https://youtube.com/..."
                                />
                              </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                <textarea 
                                    value={config.address} 
                                    onChange={(e) => setConfig({...config, address: e.target.value})}
                                    className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                    rows={3}
                                />
                            </div>
                            <button onClick={handleConfigSave} className="flex items-center gap-2 bg-temple-orange-600 text-white px-6 py-2 rounded font-bold hover:bg-temple-orange-700 mt-4">
                                <Save size={18} /> Save Changes
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'home' && (
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 space-y-8">
                        {/* 1. Hero Section */}
                        <div>
                          <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                              <ImageIcon size={20} className="text-temple-orange-600" /> Hero Banner (Main Image)
                          </h2>
                          {(banners?.length ?? 0) > 0 && (
                              <div className="space-y-4 max-w-3xl p-4 border rounded-lg bg-gray-50">
                                  <div className="relative h-48 w-full rounded overflow-hidden bg-gray-200 border border-gray-300">
                                      <img src={banners[0].imageUrl} alt="Preview" className="w-full h-full object-cover" />
                                  </div>

                                  <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Image URL / Path</label>
                                      <input 
                                          value={banners[0].imageUrl} 
                                          onChange={(e) => handleBannerChange('imageUrl', e.target.value)}
                                          className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                          placeholder="e.g., /images/hero.jpg"
                                      />
                                  </div>

                                  <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title (Eng)</label>
                                        <input 
                                            value={banners[0].title} 
                                            onChange={(e) => handleBannerChange('title', e.target.value)}
                                            className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title (Mal)</label>
                                        <input 
                                            value={banners[0].titleMal || ''} 
                                            onChange={(e) => handleBannerChange('titleMal', e.target.value)}
                                            className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                            placeholder="Malayalam Title"
                                        />
                                    </div>
                                  </div>
                                  
                                  <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tagline (Eng)</label>
                                        <input 
                                            value={banners[0].subtitle} 
                                            onChange={(e) => handleBannerChange('subtitle', e.target.value)}
                                            className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tagline (Mal)</label>
                                        <input 
                                            value={banners[0].subtitleMal || ''} 
                                            onChange={(e) => handleBannerChange('subtitleMal', e.target.value)}
                                            className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                        />
                                    </div>
                                  </div>
                              </div>
                          )}
                        </div>

                        {/* 2. Announcements */}
                        <div>
                          <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                              <Bell size={20} className="text-temple-orange-600" /> Announcements / News
                          </h2>
                          <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                             {(config.announcements || []).map((ann, idx) => (
                               <div key={idx} className="flex flex-col md:flex-row gap-2 items-start border-b pb-4 last:border-0 last:pb-0">
                                  <div className="flex-1 w-full">
                                    <input 
                                      value={ann} 
                                      onChange={(e) => handleUpdateAnnouncement(idx, 'en', e.target.value)}
                                      className="w-full border p-2 rounded mb-1 text-sm"
                                      placeholder="English Announcement"
                                    />
                                    <input 
                                      value={config.announcementsMal?.[idx] || ''} 
                                      onChange={(e) => handleUpdateAnnouncement(idx, 'ml', e.target.value)}
                                      className="w-full border p-2 rounded text-sm bg-orange-50/50"
                                      placeholder="മലയാളം അറിയിപ്പ്"
                                    />
                                  </div>
                                  <button onClick={() => handleDeleteAnnouncement(idx)} className="text-red-500 hover:text-red-700 p-2">
                                    <Trash2 size={18} />
                                  </button>
                               </div>
                             ))}
                             <button onClick={handleAddAnnouncement} className="flex items-center gap-1 text-sm font-bold text-temple-orange-600 hover:text-temple-orange-800">
                               <Plus size={16} /> Add Announcement
                             </button>
                          </div>
                        </div>

                        {/* 3. Welcome / About Text */}
                        <div>
                          <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                              <AlignLeft size={20} className="text-temple-orange-600" /> Welcome Section Text
                          </h2>
                          <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title (Eng)</label>
                                <input 
                                  value={config.homeAboutTitle || ''} 
                                  onChange={(e) => setConfig({...config, homeAboutTitle: e.target.value})}
                                  className="w-full border p-2 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title (Mal)</label>
                                <input 
                                  value={config.homeAboutTitleMal || ''} 
                                  onChange={(e) => setConfig({...config, homeAboutTitleMal: e.target.value})}
                                  className="w-full border p-2 rounded"
                                />
                              </div>
                            </div>
                            
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content (Eng)</label>
                                <textarea 
                                  value={config.homeAboutText || ''} 
                                  onChange={(e) => setConfig({...config, homeAboutText: e.target.value})}
                                  className="w-full border p-2 rounded h-32"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content (Mal)</label>
                                <textarea 
                                  value={config.homeAboutTextMal || ''} 
                                  onChange={(e) => setConfig({...config, homeAboutTextMal: e.target.value})}
                                  className="w-full border p-2 rounded h-32"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 4. Priests */}
                        <div>
                          <h2 className="text-xl font-bold mb-4 text-gray-800">Priests (Thantri & Pujari)</h2>
                          <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Thantri Name</label>
                                <input 
                                  value={config.thantriName || ''}
                                  onChange={e => setConfig({...config, thantriName: e.target.value})}
                                  className="w-full border p-2 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Thantri Image URL</label>
                                <input 
                                  value={config.thantriImageUrl || ''}
                                  onChange={e => setConfig({...config, thantriImageUrl: e.target.value})}
                                  className="w-full border p-2 rounded"
                                  placeholder="/images/thantri.jpg or http URL"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pujari Name</label>
                                <input 
                                  value={config.pujariName || ''}
                                  onChange={e => setConfig({...config, pujariName: e.target.value})}
                                  className="w-full border p-2 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pujari Image URL</label>
                                <input 
                                  value={config.pujariImageUrl || ''}
                                  onChange={e => setConfig({...config, pujariImageUrl: e.target.value})}
                                  className="w-full border p-2 rounded"
                                  placeholder="/images/pujari.jpg or http URL"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 5. About Page Content */}
                        <div>
                          <h2 className="text-xl font-bold mb-4 text-gray-800">About Page Content</h2>
                          <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">About Title (Eng)</label>
                                <input 
                                  value={config.aboutTitle || ''}
                                  onChange={e => setConfig({...config, aboutTitle: e.target.value})}
                                  className="w-full border p-2 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">About Title (Mal)</label>
                                <input 
                                  value={config.aboutTitleMal || ''}
                                  onChange={e => setConfig({...config, aboutTitleMal: e.target.value})}
                                  className="w-full border p-2 rounded"
                                />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">About Content (Eng)</label>
                                <textarea 
                                  value={config.aboutContent || ''}
                                  onChange={e => setConfig({...config, aboutContent: e.target.value})}
                                  className="w-full border p-2 rounded h-32"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">About Content (Mal)</label>
                                <textarea 
                                  value={config.aboutContentMal || ''}
                                  onChange={e => setConfig({...config, aboutContentMal: e.target.value})}
                                  className="w-full border p-2 rounded h-32"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h2 className="text-xl font-bold mb-4 text-gray-800">Background Media Settings</h2>
                          <div className="p-4 border rounded-lg bg-gray-50 space-y-4 max-w-md">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Background Media Volume (0–1)</label>
                              <input
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={typeof config.homeMediaVolume === 'number' ? config.homeMediaVolume : 0.5}
                                onChange={(e) => setConfig({ ...config, homeMediaVolume: Math.max(0, Math.min(1, parseFloat(e.target.value || '0.5'))) })}
                                className="w-full border p-2 rounded focus:ring-2 focus:ring-orange-200 outline-none"
                                placeholder="e.g., 0.5"
                              />
                            </div>
                            <p className="text-sm text-gray-600">Aigiri Nandini media on Home uses this volume.</p>
                          </div>
                        </div>

                        {/* Master Save Button */}
                        <div className="sticky bottom-0 bg-white p-4 border-t shadow-lg flex justify-end">
                           <button onClick={handleHomeSettingsSave} className="flex items-center gap-2 bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 shadow-md transform active:scale-95 transition-all">
                                <Save size={20} /> Save All Home Settings
                           </button>
                        </div>
                    </div>
                )}

                {activeTab === 'poojas' && (
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-800">Manage Poojas</h2>
                            <button onClick={handleAddPooja} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-medium">
                                <Plus size={18} /> Add New Pooja
                            </button>
                        </div>
                        
                        {editingPooja && (
                            <div className="mb-8 bg-orange-50 p-6 rounded-xl border border-orange-200">
                                <h3 className="font-bold text-orange-900 mb-4 text-lg">Edit Pooja Details</h3>
                                <form onSubmit={handleUpdatePooja} className="grid md:grid-cols-2 gap-4">
                                    <div className="col-span-1">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Name (English)</label>
                                        <input className="w-full p-2 border rounded" value={editingPooja.name} onChange={e => setEditingPooja({...editingPooja, name: e.target.value})} />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Name (Malayalam)</label>
                                        <input className="w-full p-2 border rounded" value={editingPooja.nameMal || ''} onChange={e => setEditingPooja({...editingPooja, nameMal: e.target.value})} />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Description (English)</label>
                                        <textarea className="w-full p-2 border rounded" rows={2} value={editingPooja.description} onChange={e => setEditingPooja({...editingPooja, description: e.target.value})} />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Description (Malayalam)</label>
                                        <textarea className="w-full p-2 border rounded" rows={2} value={editingPooja.descriptionMal || ''} onChange={e => setEditingPooja({...editingPooja, descriptionMal: e.target.value})} />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Price (₹)</label>
                                        <input type="number" className="w-full p-2 border rounded" value={editingPooja.price} onChange={e => setEditingPooja({...editingPooja, price: parseInt(e.target.value)})} />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Timing</label>
                                        <input className="w-full p-2 border rounded" value={editingPooja.timing} onChange={e => setEditingPooja({...editingPooja, timing: e.target.value})} />
                                    </div>
                                    <div className="col-span-2 flex gap-3 mt-2">
                                        <button type="submit" className="bg-temple-orange-600 text-white px-6 py-2 rounded font-bold hover:bg-temple-orange-700">Update Pooja</button>
                                        <button type="button" onClick={() => setEditingPooja(null)} className="bg-gray-300 text-gray-800 px-6 py-2 rounded font-bold hover:bg-gray-400">Cancel</button>
                                    </div>
                                </form>
                            </div>
                        )}

                        <div className="space-y-4">
                            {(poojas ?? []).map(pooja => (
                                <div key={pooja.id} className="border border-gray-200 p-4 rounded-lg flex flex-col md:flex-row gap-4 items-start hover:bg-gray-50 transition-colors bg-white">
                                    <div className="flex-grow">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="font-bold text-lg text-gray-800">{pooja.name}</h3>
                                                <p className="text-sm text-gray-500">{pooja.nameMal}</p>
                                            </div>
                                            <span className="bg-green-100 text-green-800 font-bold px-2 py-1 rounded text-sm">₹{pooja.price}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-2">{pooja.description}</p>
                                    </div>
                                    <div className="flex gap-2 self-end md:self-start">
                                        <button onClick={() => setEditingPooja(pooja)} className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-2 rounded font-medium text-sm">
                                            <Pencil size={16} /> Edit
                                        </button>
                                        <button onClick={() => handleDeletePooja(pooja.id)} className="flex items-center gap-1 bg-red-50 text-red-500 hover:bg-red-100 px-3 py-2 rounded font-medium text-sm">
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'gallery' && (
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-800">Manage Gallery</h2>
                            <button onClick={handleAddGallery} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-medium">
                                <Plus size={18} /> Add Image
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {(gallery ?? []).map(item => (
                                <div key={item.id} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
                                    <img src={item.url} alt={item.caption} className="w-full h-48 object-cover" />
                                    
                                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button 
                                            onClick={() => handleDeleteGallery(item.id)}
                                            className="bg-red-600 text-white p-2 rounded-full shadow hover:bg-red-700"
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    <div className="p-3">
                                        <div className="mb-2">
                                            <label className="text-[10px] uppercase font-bold text-gray-500">English Caption</label>
                                            <input 
                                                className="w-full text-sm p-1 bg-white border rounded focus:border-orange-400 outline-none"
                                                value={item.caption}
                                                onChange={(e) => handleUpdateGalleryCaption(item.id, 'caption', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-gray-500">Malayalam Caption</label>
                                            <input 
                                                className="w-full text-sm p-1 bg-white border rounded focus:border-orange-400 outline-none"
                                                value={item.captionMal || ''}
                                                onChange={(e) => handleUpdateGalleryCaption(item.id, 'captionMal', e.target.value)}
                                                placeholder="മലയാളം"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
